import type { ReactNode } from 'react';

import { DonutRow, SubmittedMeter } from './ExamCharts';
import { formatScore, RANK_BADGE_COLOR, topWrongQuestions, wrongPercent, type ExamResults } from './examResults';

const TOP_RANKS = 3;

export interface ExamReportTabProps {
  results: ExamResults;
  /** Spinner or retry while the questions load; the tallies wait on them. */
  questionNote: ReactNode;
  activeCode: string | null;
  onOpenStudent: (code: string) => void;
}

// Báo cáo, laid out like IZTeach's: submitted, top ranks, score spread, most-missed questions
export function ExamReportTab({ results, questionNote, activeCode, onOpenStudent }: ExamReportTabProps) {
  const hardest = topWrongQuestions(results.questions, 3);
  const top = results.leaders.slice(0, TOP_RANKS);

  return (
    <>
      <SubmittedMeter submitted={results.submitted} total={results.total} status={results.status} />

      <section className="quiz-results__block">
        <h3 className="quiz-results__block-title">Học sinh xếp hạng cao nhất</h3>
        {top.length === 0 ? <p className="quiz-results__empty">Chưa có học sinh nào hoàn thành bài kiểm tra này.</p> : (
          <ol className="quiz-results__ranks">
            {top.map((row) => (
              <li key={row.code} className={row.code === activeCode ? 'quiz-results__rank-row quiz-results__rank-row--active' : 'quiz-results__rank-row'}>
                <button type="button" className="quiz-results__rank-open" aria-label={`Mở bài làm của ${row.name} lên bảng`} aria-current={row.code === activeCode ? 'true' : undefined} onClick={() => onOpenStudent(row.code)}>
                  <span className="quiz-results__rank-badge" style={{ background: RANK_BADGE_COLOR }}>{row.rank}</span>
                  <span className="quiz-results__rank-name">{row.name}</span>
                  <span className="quiz-results__rank-meta"><span className="quiz-results__rank-score">{formatScore(row.score)} điểm</span></span>
                </button>
              </li>
            ))}
          </ol>
        )}
      </section>

      <section className="quiz-results__block">
        <h3 className="quiz-results__block-title">Phân bố điểm</h3>
        <DonutRow
          slices={results.distribution}
          centre={<><span className="quiz-results__donut-caption">Điểm TB</span><span className="quiz-results__donut-value">{formatScore(results.averageScore)}<small>/10</small></span></>}
        />
        {results.leaders.length === 0 ? <p className="quiz-results__empty">Chưa có điểm.</p> : null}
      </section>

      <section className="quiz-results__block">
        <h3 className="quiz-results__block-title">Câu hỏi sai nhiều nhất</h3>
        {questionNote}
        {hardest.length === 0 ? <p className="quiz-results__empty">Chưa có câu nào bị làm sai.</p> : hardest.map((stat) => (
          <div key={stat.id} className="quiz-results__wrong">
            <div className="quiz-results__wrong-title">{stat.title}</div>
            <div className="quiz-results__wrong-figures">
              <span className="quiz-results__wrong-count">{stat.countWrongSelections}</span>
              <span className="quiz-results__wrong-percent">{wrongPercent(stat)}%</span>
            </div>
            <div className="quiz-results__wrong-track"><span className="quiz-results__wrong-fill" style={{ width: `${wrongPercent(stat)}%` }} /></div>
            <div className="quiz-results__wrong-captions">
              <span className="quiz-results__caption">Trả lời sai</span>
              <span className="quiz-results__caption">Trên số học sinh trả lời</span>
            </div>
          </div>
        ))}
      </section>
    </>
  );
}
